/**
 * Zustand der Browser-Erlaubnis für Benachrichtigungen — als Hook für den
 * Hinweis in der Oberfläche.
 *
 * Der Hook fragt NICHT von sich aus nach der Erlaubnis: Edge, Chrome und Firefox
 * verwerfen eine Anfrage ohne vorangehenden Klick still. Gefragt wird
 * ausschließlich über `erlauben()`, das an einer Schaltfläche hängt.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { pushService } from "../../services/pushService";
import { erkenneBrowser, type BrowserArt, type EinrichtungsZustand } from "./notificationSetup";

/** Wie lange ein „Später“ den Hinweis-Balken ruhigstellt. */
const SPAETER_TAGE = 7;
const SPAETER_KEY = "kmvs.notif.hinweis-spaeter";

const istIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const alsWebAppGeoeffnet = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

const erlaubnisJetzt = (): NotificationPermission | null =>
  typeof Notification === "undefined" ? null : Notification.permission;

/** Liegt ein „Später“ vor, das noch nicht abgelaufen ist? */
export const istVertagt = (jetzt: number = Date.now()): boolean => {
  try {
    const roh = localStorage.getItem(SPAETER_KEY);
    if (!roh) return false;
    return jetzt - Number(roh) < SPAETER_TAGE * 86_400_000;
  } catch {
    return false; // localStorage gesperrt — dann lieber zeigen als verstecken
  }
};

const vertagen = (): void => {
  try {
    localStorage.setItem(SPAETER_KEY, String(Date.now()));
  } catch {
    /* best effort */
  }
};

export type NotificationSetup = {
  /** `null`, solange der Zustand noch ermittelt wird. */
  zustand: EinrichtungsZustand | null;
  browser: BrowserArt;
  /** Läuft gerade eine Anfrage/Anmeldung? */
  laeuft: boolean;
  /** Fehlertext der letzten Anmeldung (nur bei `abo-fehlt` gefüllt). */
  fehler: string | null;
  /** Erlaubnis anfragen und Gerät anmelden — nur aus einem Klick heraus aufrufen. */
  erlauben: () => Promise<void>;
  /** Anmeldung wiederholen, wenn die Erlaubnis bereits vorliegt. */
  erneutVersuchen: () => Promise<void>;
  /** Hinweis-Balken für sieben Tage ruhigstellen. */
  spaeter: () => void;
  /** Wurde in dieser Sitzung bereits vertagt? */
  vertagt: boolean;
};

export const useNotificationSetup = (aktiv: boolean): NotificationSetup => {
  const [zustand, setZustand] = useState<EinrichtungsZustand | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vertagt, setVertagt] = useState(() => istVertagt());
  const browser = useRef<BrowserArt>(erkenneBrowser()).current;
  /** Wurde in dieser Sitzung schon gefragt, ohne dass sich die Erlaubnis änderte? */
  const gefragtOhneErgebnis = useRef(false);

  const ermitteln = useCallback(async (): Promise<void> => {
    const erlaubnis = erlaubnisJetzt();
    if (!pushService.supported() || erlaubnis === null) {
      setZustand(istIOS() && !alsWebAppGeoeffnet() ? "ios-webapp-noetig" : "nicht-unterstuetzt");
      return;
    }
    if (erlaubnis === "denied") {
      setZustand("blockiert");
      return;
    }
    if (erlaubnis === "default") {
      setZustand(gefragtOhneErgebnis.current ? "leise-abgefangen" : "fragen-moeglich");
      return;
    }
    // Erteilt: hängt nur noch an der Anmeldung beim Push-Dienst. `ensureSubscribed`
    // ist gegen Mehrfachaufrufe geschützt und legt das Abo an, falls es fehlt.
    try {
      const ergebnis = await pushService.ensureSubscribed();
      const reg = await navigator.serviceWorker.getRegistration();
      const abo = await reg?.pushManager.getSubscription();
      // Der Grund kommt aus dem Abo-Versuch selbst — ohne ihn stünde der Nutzer
      // vor „geht nicht“, während die eigentliche Meldung (etwa eine vom Firmennetz
      // blockierte Anmeldung beim Push-Dienst) nur in der Konsole stünde.
      if (!abo && ergebnis.fehler) setFehler(ergebnis.fehler);
      setZustand(abo ? "aktiv" : "abo-fehlt");
    } catch (err) {
      setFehler(String(err));
      setZustand("abo-fehlt");
    }
  }, []);

  useEffect(() => {
    if (!aktiv) {
      setZustand(null);
      return;
    }
    void ermitteln();
  }, [aktiv, ermitteln]);

  const erlauben = useCallback(async (): Promise<void> => {
    if (typeof Notification === "undefined") return;
    setLaeuft(true);
    setFehler(null);
    try {
      const vorher = Notification.permission;
      const ergebnis = vorher === "granted" ? "granted" : await Notification.requestPermission();
      // Unverändert „default“ heißt: der Browser hat die Nachfrage leise
      // abgefangen (Edge/Chrome zeigen dann nur ein Glocken-Symbol).
      gefragtOhneErgebnis.current = ergebnis === "default";
      if (ergebnis === "granted") {
        const abo = await pushService.ensureSubscribed({ interactive: true });
        if (!abo.ok && abo.fehler) setFehler(abo.fehler);
      }
    } catch (err) {
      setFehler(String(err));
    } finally {
      setLaeuft(false);
      await ermitteln();
    }
  }, [ermitteln]);

  const erneutVersuchen = useCallback(async (): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      const abo = await pushService.ensureSubscribed({ interactive: true });
      if (!abo.ok && abo.fehler) setFehler(abo.fehler);
    } catch (err) {
      setFehler(String(err));
    } finally {
      setLaeuft(false);
      await ermitteln();
    }
  }, [ermitteln]);

  const spaeter = useCallback((): void => {
    vertagen();
    setVertagt(true);
  }, []);

  return { zustand, browser, laeuft, fehler, erlauben, erneutVersuchen, spaeter, vertagt };
};
